import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { Campaign, DisplayBannerResponse, EligibilityResponse, EligibleRewardResponse, GenericResponse, RewardEligibilityType, RewardMode, RewardTransactionsResponse, RewardsTransactionRequestParams, RewardsWalletBalanceResponse } from '../../shared/models/rewards.model';
import { StringHelper } from '../helpers/strings.helper';

@Injectable({
	providedIn: 'root',
})
export class RewardsService {
	constructor(private apiClient: ApiClientHelper, public config: AppConfig) {}

	fetchActiveCampaigns(): Promise<GenericResponse<Campaign[]>> {
		const url = this.config.REWARD_SERVICE_URL + `/api/v1/campaigns/active`;
		return this.apiClient.getForPromise(url);
	}

	fetchApplicableAccountDetails() {
		return this.apiClient.getForPromise(`${this.config.MONIEPOINT_HOST}/api/v1/wallets/balances?internal=false`);
	}

  fetchCampaignDetails(campaignIdentifier: string): Promise<GenericResponse<Campaign>> {
		const url = `${this.config.REWARD_SERVICE_URL}/api/v1/campaigns/${campaignIdentifier}`;
		return this.apiClient.getForPromise(url);
	}

  fetchRewardsTransactions(params: RewardsTransactionRequestParams): Promise<GenericResponse<RewardTransactionsResponse>>
  {
		const payload = StringHelper.toUrlString(params);
    const url = `${this.config.REWARD_SERVICE_URL}/api/v1/reward-account/account-statement?${payload}`;
		return this.apiClient.getForPromise(url);
  }

  fetchTotalRewardsEarnedToday(): Promise<GenericResponse<RewardsWalletBalanceResponse>>
  {
    const url = `${this.config.REWARD_SERVICE_URL}/api/v1/reward-account/total-reward?type=DAILY`;
		return this.apiClient.getForPromise(url);
  }

  performCashout(): Promise<GenericResponse<unknown>>
  {
    const url = `${this.config.REWARD_SERVICE_URL}/api/v1/reward/cash-out`;
		return this.apiClient.postForPromise(url, {});
  }

  async isValidBase64Image(base64String: string): Promise<boolean> {
    if (!base64String || typeof base64String !== 'string') {
      return false;
    }

    if (!this.isBase64String(base64String)) {
      return false;
    }

    // Check if the image can be loaded/rendered
    return await this.loadImage(base64String);
  }

  private isBase64String(str: string): boolean {
    const regex = /^data:image\/(png|jpg|jpeg|gif);base64,([A-Za-z0-9+/=])+$/;
    return regex.test(str);
  }

  loadImage(base64String: string): Promise<boolean> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = base64String;
    });
  }

  fetchCustomerEligibility(type: RewardEligibilityType): Promise<GenericResponse<EligibilityResponse>> {
    const url = `${this.config.REWARD_SERVICE_URL}/api/v1/customer/reward?type=${type}`;
		return this.apiClient.getForPromise(url);
  }

  fetchDisplayBanner(): Promise<GenericResponse<DisplayBannerResponse>> {
    const url = `${this.config.REWARD_SERVICE_URL}/api/v1/customer/display-banner`;
		return this.apiClient.getForPromise(url);
  }

  eligiblePromos(promos: EligibleRewardResponse[]) {
    return promos.filter((promo) => {
        const isRewardIndefinitely = promo.rewardMode === RewardMode.REWARD_INDEFINITELY;
        const hasRemainingCount = promo.maxCount - promo.totalCount > 0;
        return isRewardIndefinitely || hasRemainingCount;
    });
  }

}
